/**
 * Rows of the scadenzario (Tesoreria, 3.25): what will be collected or paid,
 * and when. Pure rules that turn invoices, rate and costs of the jobs into
 * rows; the amount is signed (positive = collection, negative = payment).
 */
import { z } from 'zod';
import { addDays, parseIsoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
import { vatOf } from './job-cycles.js';
import { invoiceStatus, selfBillingDue, } from './treasury.js';
import { idSchema, isoDateSchema } from './validation.js';
/** 2026-10-05 -> 05/10/2026 */
function italianDate(value) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
}
const DOCUMENT_PREFIX = {
    fattura: 'FT',
    nota_credito: 'NC',
    proforma: 'PF',
};
/**
 * The rows of one invoice: one per due date with something left (or zeroed by
 * an advance). The collections and payments cover the oldest dues first; an
 * advance still open comes off the last open dues, never below zero, so the
 * rows of an invoice never add up to more than what is left of it.
 */
export function invoiceScheduleRows(invoice, today) {
    // Only the received self-billings are followed: an issued one is not a cash movement of ours.
    if (invoice.void || (invoice.selfBilling && invoice.direction === 'emessa'))
        return [];
    // A self-billing is paid for its taxable (3.17).
    const adjusted = selfBillingDue(invoice);
    const netDue = invoice.selfBilling ? invoice.taxable : invoice.netDue;
    const status = invoiceStatus({
        date: invoice.date,
        netDue,
        dues: adjusted.dues,
        settlements: invoice.settlements,
        today,
    });
    // Issued = money in, received = money out; a credit note flips it through its negative amounts.
    const direction = invoice.direction === 'emessa' ? 1 : -1;
    const unit = toCents(netDue) < 0 ? -1 : 1;
    // Gestify dues that do not add up to netDue are left as invoiceStatus gives them.
    // Work on magnitudes: what is still to move on each due.
    const open = status.dues.map((due) => Math.max(0, toCents(due.residual) * unit));
    const covered = open.map(() => false);
    let advance = Math.abs(toCents(invoice.advancedOpen));
    for (let index = open.length - 1; index >= 0 && advance > 0; index -= 1) {
        if (open[index] <= 0)
            continue;
        const taken = Math.min(open[index], advance);
        open[index] = open[index] - taken;
        advance -= taken;
        covered[index] = true;
    }
    const rows = [];
    status.dues.forEach((due, index) => {
        const cents = open[index] * unit * direction;
        if (cents === 0 && !covered[index])
            return;
        const marks = [];
        if (covered[index])
            marks.push('anticipata');
        if (invoice.intercompany)
            marks.push('intergruppo');
        if (invoice.kind === 'nota_credito')
            marks.push('nota_credito');
        rows.push({
            key: `fattura:${invoice.id}:${index + 1}`,
            sourceKind: invoice.kind === 'proforma' ? 'proforma' : 'fattura',
            sourceId: invoice.id,
            subjectId: invoice.subjectId,
            subjectName: invoice.subjectName,
            document: `${DOCUMENT_PREFIX[invoice.kind]} ${invoice.number} del ${italianDate(invoice.date)}`,
            link: `/tesoreria/fatture/${invoice.id}`,
            dueDate: due.dueDate,
            amount: fromCents(cents),
            accountCode: invoice.accountCode,
            inderogabile: false,
            marks,
        });
    });
    return rows;
}
/**
 * The part of a rata of a commessa not yet invoiced nor covered by its linked pro-forma, with its
 * VAT; the invoiced part is in the invoice rows, the pro-forma has its own row.
 */
export function installmentScheduleRow(rata) {
    if (!rata.fromOffer && !rata.contracted)
        return null;
    const open = toCents(rata.amount) - toCents(rata.invoiced) - toCents(rata.proformaTaxable);
    if (open <= 0)
        return null;
    const total = rata.splitPayment ? open : open + toCents(vatOf(fromCents(open), rata.vatRate));
    const waiting = rata.kind === 'erogazione' && !rata.releasedOn;
    const marks = [];
    if (!rata.fromOffer && rata.contracted)
        marks.push('contrattualizzata');
    if (waiting)
        marks.push('all_erogazione');
    return {
        key: `rata_commessa:${rata.id}`,
        sourceKind: 'rata_commessa',
        sourceId: rata.id,
        subjectId: rata.clientId,
        subjectName: rata.clientName,
        document: `${rata.jobCode} — ${rata.description}`,
        link: `/commesse/${rata.jobId}`,
        dueDate: waiting ? null : rata.collectOn,
        amount: fromCents(total),
        accountCode: null,
        inderogabile: false,
        marks,
    };
}
/** The part of a cost of a job not yet invoiced: taxable plus VAT less the withholding, as a payment. */
export function costScheduleRow(cost) {
    if (!cost.contracted || cost.settled)
        return null;
    const open = toCents(cost.amount) - toCents(cost.invoiced) - toCents(cost.proformaTaxable);
    if (open <= 0)
        return null;
    const openMoney = fromCents(open);
    const total = open +
        toCents(vatOf(openMoney, cost.vatRate)) -
        toCents(vatOf(openMoney, cost.withholdingRate));
    const waiting = cost.kind === 'erogazione' && !cost.releasedOn;
    return {
        key: `costo_commessa:${cost.id}`,
        sourceKind: 'costo_commessa',
        sourceId: cost.id,
        subjectId: cost.supplierId,
        subjectName: cost.supplierName,
        document: `${cost.jobCode} — ${cost.description}`,
        link: `${cost.jobKind === 'progetto' ? '/progetti' : '/commesse'}/${cost.jobId}`,
        dueDate: waiting ? null : cost.payOn,
        amount: fromCents(-total),
        accountCode: null,
        inderogabile: false,
        marks: waiting ? ['contrattualizzata', 'all_erogazione'] : ['contrattualizzata'],
    };
}
/** A statement older than this many days is flagged as old. */
const STALE_DAYS = 3;
/** A suggested transfer is rounded up to this amount, in cents (100 EUR). */
const TRANSFER_STEP_CENTS = 10000;
/** True when the date is older than today less the tolerance (days). */
export function needsReschedule(effectiveDate, today, tolerance) {
    return effectiveDate !== null && effectiveDate < addDays(today, -tolerance);
}
/** The credit line (cents) usable on a day: 0 after its expiry. */
function creditAt(account, on) {
    const expiresOn = account.creditLineExpiresOn ?? null;
    return expiresOn !== null && on !== null && on > expiresOn ? 0 : toCents(account.creditLine);
}
/**
 * Tone of a balance (cents) against the credit line and the safety threshold of the account;
 * `on` is the day of the balance, after which an expired credit line counts as 0.
 */
export function balanceTone(balance, account, on = null) {
    if (balance < -creditAt(account, on))
        return 'oltre_fido';
    if (balance < 0)
        return 'fido';
    if (account.safetyThreshold !== null && balance < toCents(account.safetyThreshold)) {
        return 'soglia';
    }
    return 'ok';
}
const TONE_SEVERITY = { ok: 0, soglia: 1, fido: 2, oltre_fido: 3 };
function daysBetween(from, to) {
    return Math.round((parseIsoDate(to).getTime() - parseIsoDate(from).getTime()) / 86_400_000);
}
/**
 * Blocks, running balance per account and transfer suggestions of the scadenzario.
 *
 * Rows are returned in balance order: by balance date (an effective date in the past counts as
 * today), then collections, then inderogabili, then the other payments; rows without a date last.
 * A suggestion is made for each account that goes beyond its credit line; the donor is the other
 * account with the largest margin (lowest balance over the horizon less its safety threshold).
 * Suggestions are not chained: donors are not reduced across suggestions.
 */
export function buildSchedule(input) {
    const { today, tolerance, horizonEnd } = input;
    const overrides = new Map(input.overrides.map((item) => [item.key, item]));
    const accountByCode = new Map(input.accounts.map((item) => [item.code, item]));
    const built = [];
    for (const source of input.rows) {
        const override = overrides.get(source.key);
        const effectiveDate = override?.rescheduleOn ?? source.dueDate;
        let block;
        if (needsReschedule(effectiveDate, today, tolerance))
            block = 'da_riprogrammare';
        else if (effectiveDate === null)
            block = 'all_erogazione';
        else if (source.inderogabile)
            block = 'inderogabili';
        else
            block = 'ordinari';
        if (block !== 'da_riprogrammare' && effectiveDate !== null && effectiveDate > horizonEnd) {
            continue;
        }
        const proposed = source.accountCode;
        // A giroconto names its accounts: its leg on a non-followed one (Cassa) stays out of the balance.
        const followed = proposed === null || accountByCode.has(proposed) || source.sourceKind === 'giroconto'
            ? proposed
            : (input.accountFallback?.(proposed) ?? null);
        built.push({
            ...source,
            accountCode: override?.accountCode ??
                (followed !== null && (accountByCode.has(followed) || source.sourceKind === 'giroconto')
                    ? followed
                    : null) ??
                input.mainAccountCode,
            proposedAccountCode: source.accountCode,
            rescheduleOn: override?.rescheduleOn ?? null,
            note: override?.note ?? null,
            updatedBy: override?.updatedBy ?? null,
            effectiveDate,
            lateDays: source.dueDate === null ? null : daysBetween(source.dueDate, today),
            block,
            balance: null,
            tone: null,
        });
    }
    // Balance date of a row: the past counts as today; null when out of the balance.
    const balanceDate = (row) => row.effectiveDate === null ? null : row.effectiveDate < today ? today : row.effectiveDate;
    const rank = (row) => toCents(row.amount) > 0 ? 0 : row.inderogabile ? 1 : 2;
    const rows = built
        .map((row, index) => ({ row, index, date: balanceDate(row) }))
        .sort((a, b) => {
        if (a.date !== b.date) {
            if (a.date === null)
                return 1;
            if (b.date === null)
                return -1;
            return a.date < b.date ? -1 : 1;
        }
        return rank(a.row) - rank(b.row) || a.index - b.index;
    });
    const running = new Map();
    const lowest = new Map();
    const worst = new Map();
    const firstBeyond = new Map();
    const rowsOf = new Set();
    // Deepest shortfall under the credit line usable on the day, the starting balance included.
    const shortfalls = new Map();
    for (const item of input.accounts) {
        const start = item.balance === null ? 0 : toCents(item.balance);
        running.set(item.code, start);
        if (item.balance !== null) {
            lowest.set(item.code, { cents: start, date: item.balanceDate ?? today });
        }
        shortfalls.set(item.code, -creditAt(item, today) - start);
    }
    for (const { row, date } of rows) {
        const account = row.accountCode === null ? undefined : accountByCode.get(row.accountCode);
        if (!account || date === null)
            continue;
        rowsOf.add(account.code);
        const cents = running.get(account.code) + toCents(row.amount);
        running.set(account.code, cents);
        const tone = balanceTone(cents, account, date);
        shortfalls.set(account.code, Math.max(shortfalls.get(account.code), -creditAt(account, date) - cents));
        row.balance = fromCents(cents);
        row.tone = tone;
        const low = lowest.get(account.code);
        if (!low || cents < low.cents)
            lowest.set(account.code, { cents, date });
        if (TONE_SEVERITY[tone] > TONE_SEVERITY[worst.get(account.code) ?? 'ok']) {
            worst.set(account.code, tone);
        }
        if (tone === 'oltre_fido' && !firstBeyond.has(account.code)) {
            firstBeyond.set(account.code, date);
        }
    }
    const staleLimit = addDays(today, -STALE_DAYS);
    const accounts = input.accounts.map((item) => {
        const low = lowest.get(item.code);
        const startTone = balanceTone(item.balance === null ? 0 : toCents(item.balance), item, today);
        const rowTone = worst.get(item.code) ?? 'ok';
        return {
            ...item,
            stale: item.balanceDate === null || item.balanceDate < staleLimit,
            lowest: low ? { amount: fromCents(low.cents), date: low.date } : null,
            worstTone: TONE_SEVERITY[startTone] > TONE_SEVERITY[rowTone] ? startTone : rowTone,
            end: item.balance === null && !rowsOf.has(item.code) ? null : fromCents(running.get(item.code)),
        };
    });
    // Lowest point of an account over the horizon, the starting balance included (cents).
    const floorOf = (item) => {
        const start = item.balance === null ? 0 : toCents(item.balance);
        const low = lowest.get(item.code);
        return low ? Math.min(start, low.cents) : start;
    };
    const startBeyond = new Set(input.accounts
        .filter((item) => balanceTone(item.balance === null ? 0 : toCents(item.balance), item, today) ===
        'oltre_fido')
        .map((item) => item.code));
    const suggestions = [];
    for (const target of input.accounts) {
        const shortfall = shortfalls.get(target.code);
        if (shortfall <= 0)
            continue;
        const need = Math.ceil(shortfall / TRANSFER_STEP_CENTS) * TRANSFER_STEP_CENTS;
        let donor = null;
        let best = -Infinity;
        for (const other of input.accounts) {
            if (other.code === target.code)
                continue;
            const safety = Math.max(0, other.safetyThreshold === null ? 0 : toCents(other.safetyThreshold));
            const capacity = floorOf(other) - safety;
            if (capacity > best) {
                best = capacity;
                donor = other;
            }
        }
        if (!donor || best < need)
            continue;
        suggestions.push({
            fromCode: donor.code,
            toCode: target.code,
            amount: fromCents(need),
            // Already beyond the credit line at the start: the transfer is due now.
            by: startBeyond.has(target.code) ? today : (firstBeyond.get(target.code) ?? today),
        });
    }
    const result = rows.map((item) => item.row);
    return {
        rows: result,
        accounts,
        suggestions,
        toReschedule: result.filter((row) => row.block === 'da_riprogrammare').length,
    };
}
/** Horizons of the scadenzario, in days. */
export const SCHEDULE_HORIZONS = [30, 90, 180, 365];
/** The horizon the Scadenzario opens with, and the one the count in Da sistemare uses. */
export const SCHEDULE_DEFAULT_HORIZON = 90;
export const scheduleQuerySchema = z.object({
    horizon: z.coerce
        .number()
        .int()
        .refine((value) => SCHEDULE_HORIZONS.includes(value), {
        message: 'Orizzonte non valido',
    })
        .default(SCHEDULE_DEFAULT_HORIZON),
});
const accountCodeSchema = z.string().trim().min(1, { message: 'Campo obbligatorio' }).max(30, {
    message: 'Al massimo 30 caratteri',
});
/** Reschedule, account choice or note on one or more rows of the scadenzario. */
export const scheduleOverrideSchema = z
    .object({
    keys: z
        .array(z
        .string()
        .min(1, { message: 'Chiave non valida' })
        .max(120, { message: 'Chiave non valida' }))
        .min(1, { message: 'Scegli almeno una scadenza' })
        .max(500, { message: 'Al massimo 500 scadenze alla volta' })
        .refine((keys) => new Set(keys).size === keys.length, {
        message: 'Scadenze ripetute',
    }),
    rescheduleOn: isoDateSchema.nullable().optional(),
    shiftDays: z
        .number()
        .int({ message: 'Giorni non validi' })
        .min(1, { message: 'Almeno 1 giorno' })
        .max(365, { message: 'Al massimo 365 giorni' })
        .optional(),
    accountCode: accountCodeSchema.nullable().optional(),
    note: z.string().trim().max(500, { message: 'Al massimo 500 caratteri' }).nullable().optional(),
})
    .refine((value) => !(value.rescheduleOn !== undefined && value.shiftDays !== undefined), {
    message: 'Indica la nuova data oppure i giorni di spostamento, non entrambi',
    path: ['shiftDays'],
})
    .refine((value) => value.rescheduleOn !== undefined ||
    value.shiftDays !== undefined ||
    value.accountCode !== undefined ||
    value.note !== undefined, { message: 'Indica cosa cambiare' });
/** A transfer between two accounts planned from the scadenzario. */
export const plannedTransferSchema = z
    .object({
    fromCode: accountCodeSchema,
    toCode: accountCodeSchema,
    amount: z
        .string()
        .trim()
        .regex(/^\d{1,12}([.,]\d{1,2})?$/, { message: 'Importo non valido' })
        .transform((value) => fromCents(toCents(value.replace(',', '.'))))
        .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
    date: isoDateSchema,
    note: z.string().trim().max(500, { message: 'Al massimo 500 caratteri' }).nullable().optional(),
})
    .refine((value) => value.fromCode !== value.toCode, {
    message: 'I due conti devono essere diversi',
    path: ['toCode'],
});
/** The bank movement that carried out a planned transfer. */
export const plannedTransferDoneSchema = z.object({ movementId: idSchema });
//# sourceMappingURL=schedule.js.map